/**
 * Notifications API — получение и управление уведомлениями.
 *
 * Phase 48 extensions (Plan 04):
 *   - fetchNotificationsByTab(params) — filtered GET for bell tabs + /inbox page
 *   - archiveNotification / unarchiveNotification / markNotificationUnread
 *     → PATCH /notifications/:id with action whitelist (matches Plan 02 backend)
 *
 * Existing functions (fetchNotifications, markNotificationsRead,
 * markAllNotificationsRead) are preserved verbatim for backward compat.
 */
import apiClient from './client.js';

export async function fetchNotifications (limit = 30) {
  const { data } = await apiClient.get('/notifications', { params: { limit } });
  return data;
}

export async function markNotificationsRead (ids) {
  const { data } = await apiClient.post('/notifications/read', { ids });
  return data;
}

export async function markAllNotificationsRead () {
  const { data } = await apiClient.post('/notifications/read-all');
  return data;
}

/**
 * Fetch notifications filtered by tab (messages / reminders / archive) or inbox scope.
 * Returns { ok, notifications, unreadCount, counts:{ messages, reminders, total } }.
 *
 * @param {Object} params
 * @param {'messages'|'reminders'|'archive'|undefined} params.tab
 * @param {number} [params.limit=30]
 * @param {number} [params.offset=0]
 * @param {'email'|'instagram'|'instagram_wazzup'|'instagram_native'|'tiktok'|undefined} [params.channel]
 * @param {boolean} [params.unreadOnly]
 * @param {string} [params.from] — ISO date string
 * @param {string} [params.to]   — ISO date string
 * @param {'inbox'|undefined} [params.scope] — forces own-only MANAGER/TEAM_LEAD scope (D-13)
 */
export async function fetchNotificationsByTab (params = {}) {
  const { tab, limit = 30, offset = 0, channel, unreadOnly, from, to, scope, groupBy } = params;
  const { data } = await apiClient.get('/notifications', {
    params: { tab, limit, offset, channel, unreadOnly, from, to, scope, groupBy },
  });
  return data;
}

export async function archiveNotification (id) {
  const { data } = await apiClient.patch(`/notifications/${id}`, { action: 'archive' });
  return data;
}

export async function unarchiveNotification (id) {
  const { data } = await apiClient.patch(`/notifications/${id}`, { action: 'unarchive' });
  return data;
}

export async function markNotificationUnread (id) {
  const { data } = await apiClient.patch(`/notifications/${id}`, { action: 'mark-unread' });
  return data;
}

export async function fetchLeadNotifications (leadId) {
  const { data } = await apiClient.get('/notifications', { params: { leadId, limit: 100 } });
  return data;
}

export async function fetchUnreadByLead () {
  const { data } = await apiClient.get('/notifications/unread-by-lead');
  return data;
}

export async function markLeadNotificationsRead (leadId, types) {
  const { data } = await apiClient.post('/notifications/read', { leadId, types });
  return data;
}

export async function archiveLeadNotifications (leadId) {
  const { data } = await apiClient.post('/notifications/archive', { leadId });
  return data;
}

export async function archiveAllNotifications () {
  const { data } = await apiClient.post('/notifications/archive', { all: true });
  return data;
}
